import { NotifyHelper } from 'classes/helpers/notify.helper';
import { VideoOptionsHelper } from 'classes/helpers/video-options.helper';
import { addMinutes, isFuture, parseISO, subHours } from 'date-fns';
import { CrudAction } from 'enums/tables';
import { t } from 'i18next';
import {
  IVideoOption,
  IVideoOptionsRequest,
  IVideoOptionsResult,
} from 'interfaces/i-video-options';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { VideoHelper } from 'lib_ts/classes/video.helper';
import { PitchType } from 'lib_ts/enums/pitches.enums';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { ForUpdate, Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IVideo, IVideoPlayback } from 'lib_ts/interfaces/i-video';
import { clone, compact } from 'lodash';
import { VideosService } from 'services/videos.service';
import { create } from 'zustand';

const CONTEXT_NAME = 'VideosStore';

export const STATIC_PREFIX = 'videos/static/';

export const OPTION_DIVIDER_ID = '----divider----';

interface IPlaybackDictionary {
  [video_id: string]: { expires: Date; playback: IVideoPlayback };
}

const inFlightPlaybackQueries: DictOf<Promise<IVideoPlayback>> = {};
// shouldn't be referenced directly, use getCachedPlayback instead
const playbackDict: IPlaybackDictionary = {};

// * skip filter => videos remain selectable for the input even if a delivery type is added/changed after assigning to pitches
const FILTER_BY_DELIVERY_TYPE = false;

interface IFilter {
  teamName: string[];
  pitcher: string[];
  type: PitchType[];
  delivery: string[];
  dateAdded: string[];
}

interface ICrudConfig {
  action: CrudAction;
  models: IVideo[];
  onClose?: () => void;
}

type VideosState = {
  videos: IVideo[];
  filter: IFilter;
  loading: boolean;
  selectedVideo: IVideo | undefined;
  dialogCRUD:
    | (ICrudConfig & {
        key: number;
      })
    | undefined;
};

type VideosActions = {
  lazyLoadPlayback: (
    videoID: Nullable<string>
  ) => Promise<IVideoPlayback | undefined>;

  /** sorts videos by right (non-positive, including 0) or left (strictly positive) release to match sign of input px */
  getVideoOptions: (config: IVideoOptionsRequest) => IVideoOptionsResult;

  updateVideos: (payload: ForUpdate<IVideo>[]) => Promise<boolean>;

  mergeFilter: (value: Partial<IFilter>) => void;

  refresh: () => Promise<void>; // fetch immediately (drop lastFetched indirection)
};

const RECENTLY_UPLOADED_THRESHOLD_HOURS = 24;
const RECENTLY_UPLOADED_GROUP_LABEL = 'videos.recently-uploaded';

const getVideoGroup = (v: IVideo, checkRecentlyUploaded: boolean) => {
  const created = parseISO(v._created);
  const recentlyUploadedThreshold = subHours(
    new Date(),
    RECENTLY_UPLOADED_THRESHOLD_HOURS
  );

  if (checkRecentlyUploaded && created > recentlyUploadedThreshold) {
    // "Recently Uploaded"
    return RECENTLY_UPLOADED_GROUP_LABEL;
  }

  // "RHP: John Doe"
  const pitcherHand = BallHelper.getPitcherHand(v.ReleaseSide);

  return `${pitcherHand}${v.PitcherFullName ? `: ${v.PitcherFullName}` : ''}`;
};

export const useVideosStore = create<VideosState & VideosActions>(
  (set, get) => ({
    videos: [],
    filter: {
      teamName: [],
      pitcher: [],
      type: [],
      delivery: [],
      dateAdded: [],
    },
    loading: false,
    selectedVideo: undefined,
    playbackDict: {},
    crud: undefined,
    dialogCRUD: undefined,

    lazyLoadPlayback: async (videoID) => {
      try {
        if (!videoID) {
          return undefined;
        }

        const existing = playbackDict[videoID];

        if (existing && isFuture(existing.expires)) {
          // just use cached values
          return existing.playback;
        }

        const inFlightQuery = inFlightPlaybackQueries[videoID];

        if (inFlightQuery) {
          console.debug(
            `${CONTEXT_NAME} is already loading playback for ${videoID}, returning existing promise`
          );
          return inFlightQuery;
        }

        const newQuery = (async () => {
          try {
            const result =
              await VideosService.getInstance().getPlayback(videoID);

            playbackDict[videoID] = {
              expires: addMinutes(new Date(), 55),
              playback: result,
            };

            return result;
          } finally {
            delete inFlightPlaybackQueries[videoID];
          }
        })();

        console.debug(
          `${CONTEXT_NAME} started loading playback for ${videoID}, returning new promise`
        );
        inFlightPlaybackQueries[videoID] = newQuery;

        return newQuery;
      } catch (e) {
        console.error(e);
        return undefined;
      }
    },

    getVideoOptions: (config) => {
      const selectable = get().videos.filter((v) => {
        if (v._id === config.currentID) {
          // always include any current selection
          return true;
        }

        if (v.video_path.startsWith(STATIC_PREFIX)) {
          // must not be a static video
          return false;
        }

        if (!v.VideoTitle) {
          // must have a title
          return false;
        }

        if (
          FILTER_BY_DELIVERY_TYPE &&
          v.DeliveryType &&
          config.suggestions &&
          v.DeliveryType !== config.suggestions.delivery
        ) {
          // must have no delivery type set or it matches the selected delivery type
          return false;
        }

        if (VideoHelper.getFatalErrors(v).length > 0) {
          // does not have any errors
          return false;
        }

        return true;
      });

      // prioritize similar video release side vs pitch release side
      selectable.sort((a, b) => {
        if (config.releasePx > 0) {
          // higher px first
          return a.ReleaseSide > b.ReleaseSide ? -1 : 1;
        }

        // lower px first
        return a.ReleaseSide > b.ReleaseSide ? 1 : -1;
      });

      const defaultOptions = selectable.map((video) => {
        const safeLabel = VideoHelper.getOptionLabel(video);

        const o: IVideoOption = {
          label: safeLabel,
          value: video._id,
          group: getVideoGroup(video, !!config.recentlyUploadedFirst),
          deliveryType: video.DeliveryType,
          video: video,
        };

        return o;
      });

      if (config.recentlyUploadedFirst) {
        defaultOptions.sort((a, b) => {
          const aIsRecentlyUploaded = a.group === RECENTLY_UPLOADED_GROUP_LABEL;
          const bIsRecentlyUploaded = b.group === RECENTLY_UPLOADED_GROUP_LABEL;

          // Sort recently uploaded videos by created date
          if (
            aIsRecentlyUploaded &&
            bIsRecentlyUploaded &&
            !!a.video._created &&
            !!b.video._created
          ) {
            return a.video._created < b.video._created ? 1 : -1;
          }

          if (aIsRecentlyUploaded) {
            return -1;
          }

          return 1;
        });
      }

      const suggestedIDs = config.suggestions
        ? VideoOptionsHelper.findSuggestions({
            videos: selectable,
            request: config.suggestions,
          })
        : [];

      const suggestedOptions = compact(
        suggestedIDs.map((id) => {
          const video = selectable.find((v) => v._id === id);

          if (!video) {
            return undefined;
          }

          const o: IVideoOption = {
            label: VideoHelper.getOptionLabel(video),
            value: video._id,
            group: t('common.suggested').toString(),
            deliveryType: video.DeliveryType,
            video: video,
          };

          return o;
        })
      );

      const output: IVideoOptionsResult = {
        options: defaultOptions,
        suggestions: suggestedOptions,
      };

      return output;
    },

    updateVideos: async (payloads) => {
      try {
        set({ loading: true });

        const sanitizedPayloads = payloads.map((p) => ({
          ...p,
          PitcherFullName: p.PitcherFullName?.trim(),
          TeamName: p.TeamName?.trim() ?? null,
          VideoTitle: p.VideoTitle?.trim() ?? null,
          VideoFileName: p.VideoFileName?.trim(),
        }));

        const results = await VideosService.getInstance()
          .updateMany(sanitizedPayloads)
          .finally(() => set({ loading: false }));

        if (!results) {
          throw new Error('Failed to update many videos');
        }

        set((prev) => {
          const nextVideos = clone(prev.videos);

          results.forEach((v) => {
            const index = nextVideos.findIndex((m) => m._id === v._id);

            if (index === -1) {
              // shouldn't trigger
              return;
            }

            // replace it
            nextVideos[index] = v;
          });

          return { videos: nextVideos };
        });

        NotifyHelper.success({
          message_md: t('common.x-updated-successfully', {
            x: t(payloads.length === 1 ? 'videos.video' : 'videos.videos'),
          }),
        });

        return true;
      } catch (e) {
        console.error(e);

        NotifyHelper.error({
          message_md: t('common.request-failed-msg'),
        });

        return false;
      }
    },

    mergeFilter: (value) => set((s) => ({ filter: { ...s.filter, ...value } })),

    refresh: async () => {
      try {
        set({ loading: true });

        const results = await VideosService.getInstance()
          .getVisible()
          .finally(() => set({ loading: false }));

        set((s) => ({
          videos: results,
          // ? ensure that the selected video is still available to the user
          selectedVideo: results.find((v) => v._id === s.selectedVideo?._id),
        }));
      } catch (e) {
        console.error(e);
      }
    },
  })
);
